import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Alert, Button, Input, Space, Spin, Tag, Typography } from 'antd'
import { FolderOpenOutlined, PlusOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons'
import { translate } from '../../../../core'
import { aiAssistantService } from '../service'
import { ChatTurn, SavedReport, SavedReportRun } from '../type/ai-assistant'
import { buildHistory, MAX_MESSAGE_CHARS, requestErrorMessage } from '../ai-assistant.utils'
import { AnswerCard } from './AnswerCard'
import { SavedReportsDrawer } from './SavedReportsDrawer'
import styles from './AiAssistant.module.scss'

const EXAMPLES = [
  'How many orders were placed in 2024?',
  'Top 5 customers by total spend, all time',
  'Total sales revenue by month in 2024',
  'Which vendors have the most units in stock?',
]

/**
 * AI Reports (beta) — ask the reporting agent questions in plain English. The
 * whole conversation lives in this page's state and is sent with every question;
 * refreshing the page starts a new conversation.
 */
export const AiAssistantPage = () => {
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [draft, setDraft] = useState('')
  const [savedOpen, setSavedOpen] = useState(false)
  const nextId = useRef(1)
  const bottom = useRef<HTMLDivElement>(null)
  const pending = turns.some((turn) => turn.pending)

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' })
  }, [turns])

  const ask = async (text: string, before: ChatTurn[] = turns) => {
    const question = text.trim()
    if (!question || pending) return

    const id = nextId.current++
    setTurns([...before, { id, question, pending: true }])
    setDraft('')

    try {
      const response = await aiAssistantService.chat({ messages: buildHistory(before, question) })
      setTurns((current) => current.map((turn) => (turn.id === id ? { ...turn, pending: false, response } : turn)))
    } catch (error) {
      const message = requestErrorMessage(error)
      setTurns((current) => current.map((turn) => (turn.id === id ? { ...turn, pending: false, error: message } : turn)))
    }
  }

  const retry = (turn: ChatTurn) => {
    const index = turns.findIndex((t) => t.id === turn.id)
    ask(turn.question, turns.slice(0, index))
  }

  const showSavedRun = (report: SavedReport, run: SavedReportRun) => {
    setTurns((current) => [
      ...current,
      {
        id: nextId.current++,
        question: report.question,
        pending: false,
        response: {
          answer: `**${report.name}** — ${translate('ai_reports_saved_report_note')}`,
          verdict: 'answered',
          trace: [],
          result: run,
          queryLogId: run.queryLogId,
        },
      },
    ])
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      ask(draft)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Space align="center">
          <Typography.Title level={4} className="m-0">
            {translate('ai_reports')}
          </Typography.Title>
          <Tag color="purple">{translate('ai_reports_beta')}</Tag>
        </Space>
        <Space>
          <Button icon={<FolderOpenOutlined />} onClick={() => setSavedOpen(true)}>
            {translate('ai_reports_saved_reports')}
          </Button>
          <Button icon={<PlusOutlined />} disabled={!turns.length || pending} onClick={() => setTurns([])}>
            {translate('ai_reports_new_conversation')}
          </Button>
        </Space>
      </div>

      <Alert className="mb-3" type="info" showIcon message={translate('ai_reports_hint')} />

      <div className={styles.conversation}>
        {turns.length === 0 && (
          <div className={styles.examples}>
            <Typography.Text type="secondary">{translate('ai_reports_try')}</Typography.Text>
            <Space wrap className="mt-2">
              {EXAMPLES.map((example) => (
                <Button key={example} onClick={() => ask(example)}>
                  {example}
                </Button>
              ))}
            </Space>
          </div>
        )}

        {turns.map((turn) => (
          <div key={turn.id} className={styles.turn}>
            <div className={styles.question}>{turn.question}</div>

            {turn.pending && (
              <div className={styles.answerCard}>
                <Spin size="small" /> <Typography.Text type="secondary">{translate('ai_reports_thinking')}</Typography.Text>
              </div>
            )}

            {turn.response && <AnswerCard response={turn.response} />}

            {turn.error && (
              <Alert
                type="error"
                showIcon
                message={turn.error}
                action={
                  turn.id === turns[turns.length - 1].id && (
                    <Button size="small" icon={<ReloadOutlined />} disabled={pending} onClick={() => retry(turn)}>
                      {translate('ai_reports_retry')}
                    </Button>
                  )
                }
              />
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>

      <SavedReportsDrawer open={savedOpen} onClose={() => setSavedOpen(false)} onRan={showSavedRun} />

      <div className={styles.composer}>
        <Input.TextArea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={translate('ai_reports_placeholder')}
          autoSize={{ minRows: 1, maxRows: 6 }}
          maxLength={MAX_MESSAGE_CHARS}
          disabled={pending}
        />
        <Button type="primary" icon={<SendOutlined />} loading={pending} disabled={!draft.trim()} onClick={() => ask(draft)}>
          {translate('send')}
        </Button>
      </div>
    </div>
  )
}
